import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { CommercialInvoiceAnalysisResult } from "../../../features/commercialInvoiceAnalysis/types";

export type AnalysisPhase =
  | "idle"
  | "uploading"
  | "extracting"
  | "analyzing"
  | "mapping"
  | "complete"
  | "error";

interface BookingWizardV2ContextType {
  v2Step: number;
  setV2Step: (step: number) => void;
  analysisResult: CommercialInvoiceAnalysisResult | null;
  setAnalysisResult: (result: CommercialInvoiceAnalysisResult | null) => void;
  analysisPhase: AnalysisPhase;
  setAnalysisPhase: (phase: AnalysisPhase) => void;
  analysisError: string | null;
  setAnalysisError: (error: string | null) => void;
  missingFields: string[];
  setMissingFields: (fields: string[]) => void;
  uploadedFileName: string | null;
  setUploadedFileName: (name: string | null) => void;
}

const BookingWizardV2Context = createContext<
  BookingWizardV2ContextType | undefined
>(undefined);

export const V2_TOTAL_STEPS = 4;

export const BookingWizardV2Provider = ({
  children,
  initialStep = 0,
}: {
  children: ReactNode;
  initialStep?: number;
}) => {
  const [v2Step, setV2Step] = useState(initialStep);
  const [analysisResult, setAnalysisResult] =
    useState<CommercialInvoiceAnalysisResult | null>(null);
  const [analysisPhase, setAnalysisPhase] = useState<AnalysisPhase>("idle");
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

  const value = useMemo(
    () => ({
      v2Step,
      setV2Step,
      analysisResult,
      setAnalysisResult,
      analysisPhase,
      setAnalysisPhase,
      analysisError,
      setAnalysisError,
      missingFields,
      setMissingFields,
      uploadedFileName,
      setUploadedFileName,
    }),
    [
      v2Step,
      analysisResult,
      analysisPhase,
      analysisError,
      missingFields,
      uploadedFileName,
    ]
  );

  return (
    <BookingWizardV2Context.Provider value={value}>
      {children}
    </BookingWizardV2Context.Provider>
  );
};

export const useBookingWizardV2 = () => {
  const context = useContext(BookingWizardV2Context);
  if (!context) {
    throw new Error(
      "useBookingWizardV2 must be used within BookingWizardV2Provider"
    );
  }
  return context;
};
